import { makeStaticStyles, makeStyles, mergeClasses, useAnnounce } from '@fluentui/react-components';
import { useEffect, useMemo, useRef } from 'react';
import { CookiesProvider } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { useGetAppVersionCalculationReviewBuildNumber } from './api/app-version/app-version';
import { useIsFeatureEnabled } from './api/features/features';
import { useGetClaimsForCurrentUser } from './api/user/user';
import { initializeAppInsights } from './ApplicationInsightsService';
import SkipNavigationLink from './components/common/SkipNavigationLink';
import { FooterContainer } from './components/footer/FooterContainer';
import { Navbar } from './components/navbar/Navbar';
import ReportListPanel from './components/report-list-panel/ReportListPanel';
import { Topbar } from './components/topbar/Topbar';
import { FeedbackMessagesContextProvider } from './contexts/feedbackMessagesContext';
import GlobalEventHandler from './contexts/globalEventHandler';
import { NavigationBlockerProvider } from './contexts/navigationBlockerContext';
import TaxReturnStatusHandler from './contexts/taxReturnStatusHandler';
import { ToastMessageContextProvider } from './contexts/toastMessageContext';
import { useCurrentEnterprise } from './contexts/useCurrentEnterprise';
import { useIndexedDB } from './hooks/useIndexedDB';
import { useServiceDiscovery } from './hooks/useServiceDiscovery';
import { useGlobalClickTracker } from './telemetry/hooks/useGlobalClickTracker';
import { initializePostHog, registerEnterpriseGroup } from './telemetry/posthog';
import { PostHogEnabled } from './utils/FeatureFlags.constants';
import { DB_FORM_CACHE_NAME, getFormCacheStoreName } from './utils/FormIndexedDBUtils';
import { DB_VERSION } from './utils/IndexedDBSchemaRegistry';
import { getMessageFromUrl } from './utils/NavigationUtils';
import { HEADER_SIZE } from './utils/Style.constants';
import { DB_WHITEPAPERREPORT_NAME, getWhitePaperCacheStoreName } from './utils/WhitePaperIndexedDBUtils';

const mainContentContainerId = 'main-content-container';

// OverlayDrawer portals to document.body — keep z-index above app chrome
const useFluentGlobalStyles = makeStaticStyles({
    '.fui-OverlayDrawer': {
        zIndex: '999 !important',
    },
    '.fui-OverlayDrawer__backdrop': {
        zIndex: '999 !important',
    },
});

const useStyles = makeStyles({
    root: {
        height: '100vh',
        width: '100vw',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '& main': {
            position: 'relative',
        },
    },
    horizontal: {
        display: 'flex',
        flexDirection: 'row',
    },
    mainContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflowX: 'hidden',
        overflowY: 'hidden',
    },
    tabsWrapper: {
        flexShrink: 1,
    },
    tableWrapper: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: '450px',
        flexGrow: 1,
    },
    routerOutletLayout: {
        padding: '0 0',
        margin: '0px',
        border: 'none',
    },
    navbarWrapper: {
        flexShrink: 0,
    },
    topbarWrapper: {
        flexShrink: 0,
        minHeight: HEADER_SIZE,
        maxHeight: HEADER_SIZE,
        '> div': {
            paddingLeft: '10px',
            minHeight: HEADER_SIZE,
            maxHeight: HEADER_SIZE,
        },
    },
    mainContent: {
        height: `calc(100vh - ${HEADER_SIZE})`,
    },
});

function App() {
    useFluentGlobalStyles();
    const styles = useStyles();
    const { appInsightsConnectionString, posthogProjectToken, posthogHost } = useServiceDiscovery();
    const { enterprise } = useCurrentEnterprise();
    const { data: isPostHogFeatureEnabled } = useIsFeatureEnabled({ featureFlagName: PostHogEnabled });
    useGlobalClickTracker();
    const { data: claims } = useGetClaimsForCurrentUser();
    const { data: buildNumber } = useGetAppVersionCalculationReviewBuildNumber();
    const location = useLocation();
    const { t } = useTranslation();
    const { announce } = useAnnounce();
    const mainContentRef = useRef<HTMLDivElement>(null);

    // IndexedDB hook for cache management
    const { initializeStore } = useIndexedDB(window.indexedDB, DB_VERSION);

    useEffect(() => {
        if (appInsightsConnectionString && claims?.environmentName && buildNumber) {
            initializeAppInsights(appInsightsConnectionString, claims?.environmentName, buildNumber, claims?.userId);
        }

        if (isPostHogFeatureEnabled && claims) {
            initializePostHog({
                key: posthogProjectToken,
                host: posthogHost,
                claims,
                buildNumber,
                enterprise,
            });
        }
    }, [appInsightsConnectionString, claims, buildNumber, isPostHogFeatureEnabled, posthogProjectToken, posthogHost, enterprise]);

    useEffect(() => {
        if (isPostHogFeatureEnabled) {
            registerEnterpriseGroup(enterprise);
        }
    }, [enterprise, isPostHogFeatureEnabled]);

    useEffect(() => {
        const storeName = getFormCacheStoreName(claims);
        const whitePaperStoreName = getWhitePaperCacheStoreName(claims);

        const initPromises = [];
        if (storeName) {
            initPromises.push(initializeStore(DB_FORM_CACHE_NAME, storeName));
        }
        if (whitePaperStoreName) {
            initPromises.push(initializeStore(DB_WHITEPAPERREPORT_NAME, whitePaperStoreName));
        }

        if (initPromises.length > 0) {
            Promise.all(initPromises).catch((error) => {
                console.error('Error initializing IndexedDB:', error);
            });
        }
    }, [claims]);

    const routeAnnouncement = useMemo(() => getMessageFromUrl(location.pathname, t), [location.pathname, t]);

    useEffect(() => {
        if (!routeAnnouncement) return;
        announce(routeAnnouncement, { batchId: location.pathname });
    }, [routeAnnouncement, location.pathname, announce]);

    const isAuthenticated = !!claims?.userId;

    return (
        <div data-testid='RootApp' className={styles.root}>
            <CookiesProvider defaultSetOptions={{ path: '/' }}>
                <GlobalEventHandler>
                    <div className={styles.horizontal}>
                        <div className={styles.mainContentWrapper} data-is-scrollable='true'>
                            <SkipNavigationLink mainContentContainerId={mainContentContainerId}></SkipNavigationLink>
                            <div className={styles.topbarWrapper}>{isAuthenticated && <Topbar />}</div>
                            <main ref={mainContentRef}>
                                <div className={mergeClasses(styles.horizontal, styles.mainContent)}>
                                    <div className={styles.navbarWrapper}>
                                        {isAuthenticated && <Navbar containerRef={mainContentRef} />}
                                    </div>
                                    <div className={styles.tabsWrapper}>
                                        <ReportListPanel />
                                    </div>
                                    <div className={styles.tableWrapper}>
                                        <div className={styles.routerOutletLayout} id={mainContentContainerId}>
                                            <ToastMessageContextProvider>
                                                <FeedbackMessagesContextProvider>
                                                    <TaxReturnStatusHandler>
                                                        <NavigationBlockerProvider>
                                                            <Outlet />
                                                        </NavigationBlockerProvider>
                                                    </TaxReturnStatusHandler>
                                                </FeedbackMessagesContextProvider>
                                            </ToastMessageContextProvider>
                                        </div>
                                        <FooterContainer />
                                    </div>
                                </div>
                            </main>
                        </div>
                    </div>
                </GlobalEventHandler>
            </CookiesProvider>
        </div>
    );
}

export default App;
